import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/uxp-photoshop/uxp-photoshop/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "executeasmodal"
    }}>{`ExecuteAsModal`}</h1>
    <p>{`ExecuteAsModal is needed when a plugin wants to make modifications to the Photoshop state. This includes scenarios where the plugin creates or modify documents, or the plugin wishes to update UI or preference state.`}</p>
    <p>{`ExecuteAsModal is only available to a plugin that is using apiVersion 2 or higher.`}</p>
    <p>{`Only one plugin at a time can use `}<inlineCode parentName="p">{`executeAsModal`}</inlineCode>{`, and this means that executeAsModal guarantees that the plugin gets exclusive access to Photoshop.`}</p>
    <p>{`When executeAsModal is active, then Photoshop enters a modal user interaction state. This means that some menu items are disabled.
If the modal state lasts a significant amount of time (currently more than two seconds), then a progress bar is shown. The progress bar will identify the plugin that is associated with the modal state, and it will include the ability for the user to cancel the interaction. The following illustrates the progress bar that would be created for a plugin called "Sample Plugin". The progress bar is indefinite until the plugin informs Photoshop about its progress.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "960px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "22.499999999999996%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/0a90fc45778667dad31f651d6dbda964/cb523/progress-bar.webp 320w", "/photoshop/uxp/static/0a90fc45778667dad31f651d6dbda964/797b9/progress-bar.webp 640w", "/photoshop/uxp/static/0a90fc45778667dad31f651d6dbda964/6c7d1/progress-bar.webp 960w"],
            "sizes": "(max-width: 960px) 100vw, 960px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/0a90fc45778667dad31f651d6dbda964/72799/progress-bar.png 320w", "/photoshop/uxp/static/0a90fc45778667dad31f651d6dbda964/6af66/progress-bar.png 640w", "/photoshop/uxp/static/0a90fc45778667dad31f651d6dbda964/d9199/progress-bar.png 960w"],
            "sizes": "(max-width: 960px) 100vw, 960px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/photoshop/uxp/static/0a90fc45778667dad31f651d6dbda964/d9199/progress-bar.png",
            "alt": "progress bar",
            "title": "progress bar",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`When a plugin is inside a modal scope, then it controls Photoshop. This means that you no longer need to use options such as `}<inlineCode parentName="p">{`modalBehavior`}</inlineCode>{` with `}<inlineCode parentName="p">{`batchPlay`}</inlineCode>{`.`}</p>
    <h2 {...{
      "id": "api"
    }}>{`API`}</h2>
    <p>{`ExecuteAsModal is exposed on the Photoshop core module.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`require('photoshop').core.executeAsModal(targetFunction, options);
`}</code></pre>
    <p>{`targetFunction is a JavaScript function that will be executed after Photoshop enters a modal state. When the targetFunction completes, then Photoshop will exit the modal state. The targetFunction can be asynchronous.`}</p>
    <p>{`Only one plugin can be modal at any given time. If another plugin is modal when you call executeAsModal, then executeAsModal will raise an error. It is therefore important to handle errors when calling this method.`}</p>
    <p>{`It is also recommended that JavaScript awaits on the result from executeAsModal. Without an await, JavaScript will proceed to the subsequent lines of code while Photoshop enters a modal state.`}</p>
    <p>{`A typical use case is:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`try {
    await require('photoshop').core.executeAsModal(targetFunction, {"commandName": "My Script Command"});
    }
    catch(e) {
      if (e.number == 9) {
          showAlert("executeAsModal was rejected (some other plugin is currently inside a modal scope)");
      }
      else {
        // This case is hit if the targetFunction throws an exception
      }
    }
`}</code></pre>
    <h3 {...{
      "id": "details"
    }}>{`Details`}</h3>
    <p>{`executeAsModal takes the following arguments:`}</p>
    <ol>
      <li parentName="ol">{`targetFunction: The JavaScript function to execute after Photoshop enters a modal state.`}</li>
      <li parentName="ol">{`options: Options describing the request. The following properties are recognized:`}
        <ol parentName="li">
          <li parentName="ol">{`commandName (required): A string describing the command. This string is shown in the progress bar UI.`}</li>
          <li parentName="ol">{`descriptor (optional): An object with command arguments. See documentation for targetFunction below.`}</li>
          <li parentName="ol">{`interactive (optional): A boolean to toggle `}<a parentName="li" {...{
              "href": "#interactive-mode"
            }}>{`interactive mode`}</a>{`. `}</li>
        </ol>
      </li>
    </ol>
    <p>{`The JavaScript target function has the following signature:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`async function targetFunction(executionContext, descriptor)
`}</code></pre>
    <p>{`The executionContext contains functionality related to managing the modal state.`}</p>
    <p>{`The descriptor contains the values provided to the descriptor property in the options argument to executeAsModal.`}</p>
    <p>{`The executionContext contains the following properties:`}</p>
    <ul>
      <li parentName="ul">{`isCancelled: A boolean that is true if the user has cancelled the modal interaction.`}</li>
      <li parentName="ul">{`onCancel: A function property. If JavaScript assigns a function to this property, then this function is executed if the user cancels the modal interaction.`}</li>
      <li parentName="ul">{`reportProgress: A function that JavaScript can use to customize the progress bar. See below for details.`}</li>
      <li parentName="ul">{`hostControl: An object containing the following properties:`}
        <ul parentName="li">
          <li parentName="ul">{`suspendHistory. A function that can be used to suspend history on a target document. See below for details.`}</li>
          <li parentName="ul">{`resumeHistory. A function that can be used to resume history on a target document. See below for details.`}</li>
          <li parentName="ul">{`registerAutoCloseDocument. Register a document to be closed when the modal scope exits. See below for details.`}</li>
        </ul>
      </li>
      <li parentName="ul">{`unregisterAutoCloseDocument. Unregister a document from being closed when the modal scope exits. See below for details.`}</li>
    </ul>
    <h4 {...{
      "id": "user-cancellation"
    }}>{`User cancellation`}</h4>
    <p>{`executeAsModal puts Photoshop into a modal state, and it is important to allow the user to exit this state if the command was invoked by mistake, or if the command is taking too much time.`}</p>
    <p>{`The user can cancel the operation by pressing the Escape key or using cancellation UI in the progress bar.
If the user cancels the interaction, then JavaScript should return from its target function as soon as possible.
JavaScript can use `}<inlineCode parentName="p">{`isCancelled`}</inlineCode>{` and `}<inlineCode parentName="p">{`onCancel`}</inlineCode>{` on the executionContext to get information about the current user cancellation state. In addition to this, Photoshop APIs such as `}<inlineCode parentName="p">{`batchPlay`}</inlineCode>{` will raise an exception if they are invoked from a target function after the user has cancelled the interaction.`}</p>
    <p>{`The following is an example of a target JavaScript function:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`async function targetFunction(executionControl, descriptor) {
  let target = {_ref:[{_ref:"property", "_property": "hostName"}, {"_ref":"application","_enum":"ordinal","_value":"targetEnum"}]};
  let command = {"_obj": "get", "_target": target};
  while (true) {
    await psAction.batchPlay([command], {});
  }
}
await require("photoshop").core.executeAsModal(targetFunction, {"commandName": "User Cancel Test"});
`}</code></pre>
    <p>{`This sample will run until the user cancels the interaction. After the user cancels, the modal scope becomes cancelled, and the next call to batchPlay will raise an exception and exit the target function.`}</p>
    <p>{`Due to the design of the underlying JavaScript runtime, JavaScript can only be cancelled when it is `}<em parentName="p">{`interruptible`}</em>{`. JavaScript can be interrupted when it is waiting on the resolution of a promise. Without the "await" keyword in the above example, the JavaScript function would not terminate when the user cancels. Having a tight loop such as the following also does not allow for automatic cancellation of the JavaScript function.`}</p>
    <pre><code parentName="pre" {...{}}>{`async function targetFunction(executionControl) {
  while (true) {
    calculateSomeDigitsOfPi();
  }
}
`}</code></pre>
    <p>{`JavaScript that runs for a significant amount of time without an interruption point should regularly query isCancelled on the executionContext. The JavasScript example above can be made cancellable by modifying it to the following:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`async function targetFunction(executionControl) {
  while (true) {
    calculateSomeDigitsOfPi();
    if (executionContext.isCancelled) {
      throw "user cancelled";
    }
  }
}
`}</code></pre>
    <p>{`When the JavaScript function uses "await" with a Photoshop JavaScript command, then it is automatically terminated if the user cancels the operation. The automatic cancellation relies on JavaScript exceptions, and it is therefore important to not discard exceptions. The following function discards exceptions around batchPlay and will therefore `}<em parentName="p">{`not`}</em>{` be automatically terminated when the user cancels the interaction.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`async function targetFunction(executionControl) {
  let target = {_ref:[{_ref:"property", "_property": "hostName"}, {"_ref":"application","_enum":"ordinal","_value":"targetEnum"}]};
  let command = {"_obj": "get", "_target": target};
  while (true) {
    try {
       await psAction.batchPlay([command], {});
    } catch (e) {}
  }
}
await require("photoshop").core.executeAsModal(targetFunction, {"commandName": "User Cancel Test"});
`}</code></pre>
    <h4 {...{
      "id": "progress-bar"
    }}>{`Progress bar`}</h4>
    <p>{`By default, Photoshop shows an indeterminate progress bar while a modal scope is active. The progress bar is shown a few seconds after the modal scope is initiated.
JavaScript can use `}<inlineCode parentName="p">{`reportProgress`}</inlineCode>{` to customize this behavior. To obtain a determinate progress bar, JavaScript can specify a value between 0 and 1 when calling reportProgress. Example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`async function targetFunction(executionControl) {
  executionControl.reportProgress({"value": 0.3});
}
`}</code></pre>
    <p>{`Setting a value will switch the progress bar to be a determinate progress bar & show the progress bar if it is not yet visible.`}</p>
    <p>{`JavaScript can change the commandName that is shown in the progress UI by using the "commandName" property. This can be used to inform the user about the current stage of the operation. Example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`    executionControl.reportProgress({"value": 0.9, "commandName": "Finishing Up"});
`}</code></pre>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "968px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "23.4375%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/2e9adeb2a03e5b642b890042878177d5/cb523/progress-bar-2.webp 320w", "/photoshop/uxp/static/2e9adeb2a03e5b642b890042878177d5/797b9/progress-bar-2.webp 640w", "/photoshop/uxp/static/2e9adeb2a03e5b642b890042878177d5/cece5/progress-bar-2.webp 968w"],
            "sizes": "(max-width: 968px) 100vw, 968px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/2e9adeb2a03e5b642b890042878177d5/72799/progress-bar-2.png 320w", "/photoshop/uxp/static/2e9adeb2a03e5b642b890042878177d5/6af66/progress-bar-2.png 640w", "/photoshop/uxp/static/2e9adeb2a03e5b642b890042878177d5/e2310/progress-bar-2.png 968w"],
            "sizes": "(max-width: 968px) 100vw, 968px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/photoshop/uxp/static/2e9adeb2a03e5b642b890042878177d5/e2310/progress-bar-2.png",
            "alt": "progress bar",
            "title": "progress bar",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`The progress bar is hidden while modal UI is shown.`}</p>
    <h4 {...{
      "id": "interactive-mode"
    }}>{`Interactive Mode`}</h4>
    <p><em parentName="p">{`Added in Photoshop 23.3`}</em></p>
    <p>{`If a plugin requires the accepting of user input or interaction while in a executeAsModal scope, "Interactive Mode" may be required.`}</p>
    <p>{`This mode refrains from displaying a blocking progress dialog to the user, and reduces the number of restrictions that hinder accepting of user input. Use-cases for interactive mode may include:`}</p>
    <ul>
      <li parentName="ul">{`allowing users to input data into an invoked modal filter dialog`}</li>
      <li parentName="ul">{`awaiting user input on a Photoshop workspace, such as Select and Mask`}</li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`await require("photoshop").core.executeAsModal(targetFunction, {"commandName": "Apply two filters", "interactive": true});
`}</code></pre>
    <p>{`In lieu of the progress bar dialog, users can find the `}<inlineCode parentName="p">{`Cancel Plugin Command`}</inlineCode>{` menu item under the Photoshop `}<inlineCode parentName="p">{`Plugins`}</inlineCode>{` menu. This will interrupt the plugin's executeAsModal scope as described in `}<a parentName="p" {...{
        "href": "#user-cancellation"
      }}>{`User Cancellation`}</a>{`.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "287px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "50.871080139372815%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/b1c378af3bb8a709ec82abbf2e704a1f/5f53f/eam-pluginmenu-cancel.webp 287w"],
            "sizes": "(max-width: 287px) 100vw, 287px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/b1c378af3bb8a709ec82abbf2e704a1f/480fd/eam-pluginmenu-cancel.png 287w"],
            "sizes": "(max-width: 287px) 100vw, 287px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/photoshop/uxp/static/b1c378af3bb8a709ec82abbf2e704a1f/480fd/eam-pluginmenu-cancel.png",
            "alt": "cancel via plugin menu",
            "title": "cancel via plugin menu",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h4 {...{
      "id": "history-state-suspension"
    }}>{`History state suspension`}</h4>
    <p>{`The hostControl property on the executionContext can be used to suspend and resume history states. While a history state is suspended, Photoshop will coalesce all document changes into a single history state with a custom name.`}</p>
    <p>{`Example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`async function historyStateSample(executionContext) {
    let hostControl = executionContext.hostControl;

    // Get an ID for a target document
    let documentID = await getTargetDocument();

    // Suspend history state on the target document
    // This will coalesce all changes into a single history state called
    // 'Custom Command'
    let suspensionID = await hostControl.suspendHistory({
        "documentID": documentID,
        "name": "Custom Command"
    });

    // modify the document
    // . . .

    // resume the history state
    await hostControl.resumeHistory(suspensionID);
}
`}</code></pre>
    <p>{`The signature for suspendHistory is:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`executionContext.hostControl.suspendHistory(options)
`}</code></pre>
    <p>{`The options argument is an object with the following properties:`}</p>
    <ul>
      <li parentName="ul">{`documentID: The ID of the document whose history state should be suspended.`}</li>
      <li parentName="ul">{`name: The name that is used for the history state. This is visible in the History panel.
suspendHistory returns a suspension identifier. This identifier should be used with resumeHistory.`}</li>
    </ul>
    <p>{`The signature for resumeHistory is:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`executionContext.hostControl.resumeHistory(suspensionID, commit)
`}</code></pre>
    <ul>
      <li parentName="ul">{`suspensionID: the suspension identifier that was returned from suspendHistory.`}</li>
      <li parentName="ul">{`commit: if true then the current document state is committed and a history state is created. If the second argument is false, then the document state is rolled back to the time when the state was suspended. This argument is optional and the default value is true. Photoshop only creates a history state if the document was modified between the calls to suspendHistory and resumeHistory.`}</li>
    </ul>
    <p>{`When the modal scope ends, Photoshop will auto-resume history on any document that is still in a suspended state. If the target function for the modal scope returns normally, then all unsuspended states are committed. If the target function exits via an exception, then all unsuspended history states are cancelled.`}</p>
    <h4 {...{
      "id": "notifications"
    }}>{`Notifications`}</h4>
    <p>{`When executeAsModal is active, then Photoshop notifications are silenced similar to when an action is run from the actions panel.`}</p>
    <p>{`This means that other plugins cannot listen for batchPlay commands that are executed while the modal scope is active.`}</p>
    <p>{`Plugins can register for notifications related to starting and ending a modal JavaScript scope by registering for the following events:`}</p>
    <ul>
      <li parentName="ul">{`"modalJavaScriptScopeEnter"`}</li>
      <li parentName="ul">{`"modalJavaScriptScopeExit"`}</li>
    </ul>
    <h4 {...{
      "id": "automatic-document-closing"
    }}>{`Automatic document closing`}</h4>
    <p>{`When the user cancels a modal scope, then JavaScript cannot make any further document changes until it returns from the modal scope. In order to ensure proper clean up of temporary documents, JavaScript can register one or more documents to be automatically closed without saving when the modal scope ends. The following is an example of JavaScript that registers a document to be closed when the modal scope ends:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`async function modalFunction(executionContext) {
    let hostControl = executionContext.hostControl;

    let docID = await createDocument();
    await hostControl.registerAutoCloseDocument(docID);

    ...
}
`}</code></pre>
    <p>{`JavaScript can unregister a document from being automatically closed by using unregisterAutoCloseDocument. The following illustrates JavaScript that creates and marks a document as "auto close" while the method is running. If the method succeeds then the document is unregistered from the set of auto close documents. This allows JavaScript to create a complete document, or close the document is the user cancels while the script is running.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`async function modalFunction(executionContext) {
    let hostControl = executionContext.hostControl;

    let docID = await createDocument();
    await hostControl.registerAutoCloseDocument(docID);

    // Add contents to docID

    ...

    await hostControl.unregisterAutoCloseDocument(docID);
}
`}</code></pre>
    <h4 {...{
      "id": "notes"
    }}>{`Notes`}</h4>
    <p>{`You can have nested modal scopes. A target function can use executeAsModal to execute another target function.
All modal scopes share the same global modal state. This mean that any nested scope can modify the state on the (single) progress bar. Similarly, you can suspend the history state of a document in one scope, and resume the state in another.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      